import axios from "axios";
import type { TFunction } from "i18next";
import {
  getBackendErrorMessageFromUnknown,
  getBackendValidationErrorMessage,
} from "services/api/backendError";
import type { AddStaffFormData } from "./addStaffPanel.contract";
import {
  isIbanChecksumFailure,
  toPaymentMethodFallbackErrorTranslationKey,
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "../shared/staff.paymentMethod.logic";

interface CreateWorkerErrorParams {
  readonly error: unknown;
  readonly formData: AddStaffFormData;
  readonly translate: TFunction;
}

interface InvalidPaymentMethodErrorParams {
  readonly backendMessage: string | null;
  readonly formData: AddStaffFormData;
  readonly translate: TFunction;
}

const ADD_STAFF_BACKEND_MESSAGE_TO_TRANSLATION_KEY: Record<string, string> = {
  "Employee number is already in use":
    "Staff.ProfilePanelErrorEmployeeNumberAlreadyInUse",
  "Invalid payment method type": "Staff.PaymentMethodTypeInvalid",
};

function getIbanChecksumErrorMessage({
  error,
  formData,
  translate,
}: CreateWorkerErrorParams): string | null {
  const validationMessage = getBackendValidationErrorMessage({
    field: "Request.Employment.PaymentMethodValue",
    payload: axios.isAxiosError(error) ? error.response?.data : null,
  });
  const hasChecksumFailure =
    formData.paymentMethodType === "InternationalBankAccount" &&
    isIbanChecksumFailure(validationMessage);
  return hasChecksumFailure
    ? translate("Staff.PaymentMethodInternationalBankAccountChecksumInvalid")
    : null;
}

function getInvalidPaymentMethodErrorMessage({
  backendMessage,
  formData,
  translate,
}: InvalidPaymentMethodErrorParams): string | null {
  if (backendMessage !== "Invalid payment method value") return null;
  const validationResult = validatePaymentMethodValue(
    formData.paymentMethodType,
    formData.paymentMethodValue,
  );
  return validationResult.type === "failure"
    ? translate(toPaymentMethodValidationTranslationKey(validationResult.error))
    : translate(
        toPaymentMethodFallbackErrorTranslationKey(formData.paymentMethodType),
      );
}

export function resolveCreateWorkerErrorMessage({
  error,
  formData,
  translate,
}: CreateWorkerErrorParams): string {
  const ibanChecksumError = getIbanChecksumErrorMessage({
    error,
    formData,
    translate,
  });
  if (ibanChecksumError != null) return ibanChecksumError;

  const backendMessage = getBackendErrorMessageFromUnknown(error);
  const paymentMethodError = getInvalidPaymentMethodErrorMessage({
    backendMessage,
    formData,
    translate,
  });
  if (paymentMethodError != null) return paymentMethodError;

  const mappedKey = backendMessage
    ? ADD_STAFF_BACKEND_MESSAGE_TO_TRANSLATION_KEY[backendMessage]
    : null;
  if (mappedKey) return translate(mappedKey);

  return translate("Alerts.SomethingWentWrong");
}
